import { parseDbTimestamp } from "@repo/shared";
import type {
  IdentityConflictRow,
  IdentityMismatchField,
  SumsubApplicantData,
  SumsubFlowAccessTokenRequest,
} from "@repo/shared/types";
import snsWebSdk from "@sumsub/websdk";
import { History } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { IdentityConflictPanel } from "@/components/verified-id";
import { ApiError } from "@/lib/api/_client";
import { generateSumsubFlowAccessToken } from "@/lib/api/sumsub";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { ComponentStyleType } from "@/lib/types/flow";
import { getRelativeTime } from "@/lib/utils";
import {
  type ActingRepresentative,
  RepresentativeNote,
  representativeNoteText,
} from "../representative-note";
import { SumsubResults } from "./sumsub-results";
import { T } from "./sumsub-verification-widget.i18n";

type SumsubVerificationSnapshot = {
  // `true` for a freshly saved result; flipped to `false` by the test→production
  // mode-switch invalidation while the result stays visible.
  hasBeenChecked: boolean;
  searchedAt: string;
  externalUserId: string;
  applicantId: string;
  status: {
    isVerified: boolean;
    reviewStatus: string;
    reviewAnswer: string | null;
  };
  info: {
    firstName?: string;
    lastName?: string;
    dob?: string;
    country?: string;
  } | null;
  documentExpiry: string | null;
  rawData: SumsubApplicantData;
};

type VerificationState = "idle" | "loading" | "verifying" | "polling" | "completed" | "error";

type SumsubWidgetProps = {
  /** The sumsub_verification component being run; the server reads its level from it. */
  componentId: string;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  language?: string;
  inputData?: string | null;
  calculatedData?: string | null;
  /** Re-read the saved snapshot from the backend. Returns the fresh calculatedData. */
  onRefetchValue: () => Promise<string | null>;
  disabled?: boolean;
  // Public flow: raw previous answer + browser-side use-callback (legacy path).
  previousAnswer?: {
    inputData: string;
    calculatedData: string;
    timestamp: string | null;
  } | null;
  onUsePreviousAnswer?: () => Promise<void>;
  // Advisor flow: backend-sourced reuse. `available`/`sourceDate` come from the reuse resolver;
  // `onApply` calls the apply mutation and the spread value flows back via `calculatedData`.
  available?: boolean;
  sourceDate?: string | null;
  onApply?: () => Promise<void>;
  /** Flow-instance-scoped access-token URL (internal or public variant). */
  accessTokenUrl: string;
  /** Follow-up A — H2 error highlight (border + message-under-title). */
  hasValidationError?: boolean;
  /**
   * QL-f — fields where the scanned document contradicts our record. Supplied
   * by the renderer (advisor or public) because resolving them needs the
   * client, which the widget does not have. Empty on a clean check.
   */
  identityConflicts?: IdentityConflictRow[];
  onResolveIdentityConflict?: (
    field: IdentityMismatchField,
    decision: "update" | "dismiss",
  ) => Promise<void>;
  /** QP-184 — Art. 54(6): the acting representative is the person verified. */
  actingRepresentative?: ActingRepresentative | null;
  /**
   * QL-654 — several people are verified, each for themselves: this instance is
   * ONE person's (a representative's `client_party.id`); it is sent on every
   * access-token request.
   */
  subjectId?: string;
};

const MAX_POLLS_PER_ROUND = 5;
// Linear backoff between polls: 2s before #2, 4s before #3, 6s before #4, 8s
// before #5. Gives the Sumsub queue more breathing room on later attempts
// without spamming the API right after submission.
const POLL_INTERVAL_STEP_MS = 2000;

function nextPollDelayMs(attemptsCompleted: number): number {
  return POLL_INTERVAL_STEP_MS * attemptsCompleted;
}

function parseSnapshot(raw: string | null | undefined): SumsubVerificationSnapshot | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SumsubVerificationSnapshot;
  } catch {
    return null;
  }
}

function isTerminalSnapshot(snap: SumsubVerificationSnapshot | null): boolean {
  const answer = snap?.status?.reviewAnswer;
  return answer === "GREEN" || answer === "RED";
}

export function SumsubVerificationWidget({
  componentId,
  style = "none",
  title,
  description,
  language,
  calculatedData,
  onRefetchValue,
  disabled,
  previousAnswer,
  onUsePreviousAnswer,
  available = false,
  sourceDate,
  onApply,
  accessTokenUrl,
  hasValidationError = false,
  actingRepresentative,
  subjectId,
  identityConflicts,
  onResolveIdentityConflict,
}: SumsubWidgetProps) {
  const { userLanguage } = useUserLanguage();
  const { formatDate } = useRegionalFormat();
  const { resolvedTheme } = useTheme();
  const lang = language || userLanguage;
  const t = useT(T);

  const currentSnapshot = useMemo(() => parseSnapshot(calculatedData), [calculatedData]);

  // The externalUserId of the verification submission in flight. While set,
  // we only honour snapshots whose rawData.externalUserId matches — guards
  // against rendering a stale snapshot from a previous attempt during the
  // window between SDK submission and the webhook landing.
  const [submittedExternalUserId, setSubmittedExternalUserId] = useState<string | null>(null);

  const renderableSnapshot = useMemo(() => {
    if (!currentSnapshot) return null;
    if (
      submittedExternalUserId &&
      currentSnapshot.rawData?.externalUserId !== submittedExternalUserId
    ) {
      return null;
    }
    return currentSnapshot;
  }, [currentSnapshot, submittedExternalUserId]);

  // Initial state: completed if we boot with a terminal saved snapshot.
  // Note: this captures the value on first render only.
  const initialIsTerminal = useRef(isTerminalSnapshot(currentSnapshot)).current;
  const [state, setState] = useState<VerificationState>(initialIsTerminal ? "completed" : "idle");

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // True when the failure is a tenant-level Sumsub misconfiguration (disabled / not configured).
  // Retrying can't help, so we hide the "Start new test" button for these.
  const [isConfigError, setIsConfigError] = useState(false);
  const sdkInstanceRef = useRef<any>(null);

  // Polling state
  const [pollAttempt, setPollAttempt] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  // Previous answer choice screen state
  const [hasDecided, setHasDecided] = useState(false);
  const [isApplyingPrevious, setIsApplyingPrevious] = useState(false);
  const [reuseError, setReuseError] = useState<string | null>(null);

  // Refs to keep the polling effect stable across re-renders without
  // restarting whenever parent passes a new closure for onRefetchValue.
  const onRefetchValueRef = useRef(onRefetchValue);
  onRefetchValueRef.current = onRefetchValue;
  const submittedExternalUserIdRef = useRef(submittedExternalUserId);
  submittedExternalUserIdRef.current = submittedExternalUserId;

  // Reuse can be offered via the backend (advisor: `available` + `onApply`) or the legacy browser
  // path (public: `previousAnswer` + `onUsePreviousAnswer`).
  const canReuse = (available && !!onApply) || (!!previousAnswer && !!onUsePreviousAnswer);
  const reuseDate = sourceDate ?? previousAnswer?.timestamp ?? null;

  const showChoiceScreen = canReuse && !disabled && !calculatedData && !hasDecided && !reuseError;

  // Cleanup SDK on unmount
  useEffect(() => {
    return () => {
      if (sdkInstanceRef.current) {
        sdkInstanceRef.current.destroy?.();
        sdkInstanceRef.current = null;
      }
    };
  }, []);

  // When polling lands a terminal result that matches our current submission,
  // exit polling and switch to the completed view.
  useEffect(() => {
    if (state !== "polling") return;
    if (renderableSnapshot && isTerminalSnapshot(renderableSnapshot)) {
      setIsPolling(false);
      setState("completed");
    }
  }, [state, renderableSnapshot]);

  // A terminal snapshot can also appear while idle — the on-open Sumsub sync
  // (webhook fallback) writes the result after first render, or a webhook
  // updates it while the component is open. Surface it instead of staying on
  // "Start Verification".
  useEffect(() => {
    if (state === "idle" && renderableSnapshot && isTerminalSnapshot(renderableSnapshot)) {
      setState("completed");
    }
  }, [state, renderableSnapshot]);

  // Bounded polling: max MAX_POLLS_PER_ROUND attempts with linear backoff
  // between them (see nextPollDelayMs). Increments pollAttempt for the
  // "Refetching N/5" label. Stops on a matching terminal result or on
  // round exhaustion.
  useEffect(() => {
    if (state !== "polling" || !isPolling) return;

    let attemptCount = 0;
    let cancelled = false;

    async function poll() {
      if (cancelled) return;
      attemptCount += 1;
      setPollAttempt(attemptCount);

      try {
        const fresh = await onRefetchValueRef.current();
        if (cancelled) return;

        const snap = parseSnapshot(fresh);
        const expected = submittedExternalUserIdRef.current;
        const matchesCurrent = !expected || snap?.rawData?.externalUserId === expected;

        if (snap && matchesCurrent && isTerminalSnapshot(snap)) {
          setIsPolling(false);
          setState("completed");
          return;
        }
      } catch (err) {
        log.error({ err }, "sumsub polling refetch failed");
      }

      if (cancelled) return;
      if (attemptCount >= MAX_POLLS_PER_ROUND) {
        setIsPolling(false);
        return;
      }

      setTimeout(poll, nextPollDelayMs(attemptCount));
    }

    poll();

    return () => {
      cancelled = true;
    };
  }, [state, isPolling]);

  const handleStart = () => {
    initializeSdk();
  };

  const initializeSdk = async () => {
    setState("loading");
    setErrorMessage(null);
    setIsConfigError(false);

    // For public flow pages, read the session token from localStorage
    const publicToken = accessTokenUrl.includes("/public/")
      ? (localStorage.getItem("publicSessionToken") ?? undefined)
      : undefined;

    try {
      const body: SumsubFlowAccessTokenRequest = {
        componentId,
        ...(subjectId ? { subjectId } : {}),
      };
      const response = await generateSumsubFlowAccessToken(accessTokenUrl, body, publicToken);

      if (!response.success || !response.data) {
        setState("error");
        // A disabled/not-configured tenant gets a clean, explicit message and no retry button;
        // any other failure keeps the generic, retryable error.
        if (response.errorCode === "sumsub_disabled") {
          setErrorMessage(t.sumsubDisabledError);
          setIsConfigError(true);
        } else if (response.errorCode === "sumsub_not_configured") {
          setErrorMessage(t.sumsubNotConfiguredError);
          setIsConfigError(true);
        } else {
          setErrorMessage(response.error || t.failedToGenerateToken);
        }
        return;
      }

      setSubmittedExternalUserId(response.data.userId);
      launchSumsubSdk(response.data.token);
    } catch (error: any) {
      setState("error");
      setErrorMessage(error.message || t.unexpectedError);
    }
  };

  // Sumsub calls this when the access token expires mid-session. QL-590 — it
  // must be a token for the SAME applicant: one for a new applicant moves the
  // session onto a new, empty applicant, and a result already submitted for the
  // old one is dropped as superseded. Read through the ref: the SDK keeps this
  // function from launch, when the state was unset.
  const requestNewAccessToken = async (): Promise<string> => {
    const refreshExternalUserId = submittedExternalUserIdRef.current;
    if (!refreshExternalUserId) return "";
    try {
      const publicToken = accessTokenUrl.includes("/public/")
        ? (localStorage.getItem("publicSessionToken") ?? undefined)
        : undefined;
      const response = await generateSumsubFlowAccessToken(
        accessTokenUrl,
        {
          componentId,
          refreshExternalUserId,
          ...(subjectId ? { subjectId } : {}),
        } satisfies SumsubFlowAccessTokenRequest,
        publicToken,
      );
      return response.success && response.data ? response.data.token : "";
    } catch (err) {
      // `post` throws on any non-2xx. A 4xx is the server refusing the refresh
      // (the attempt was replaced by a newer one, or is gone), not a failure.
      if (err instanceof ApiError && err.status < 500) {
        log.warn({ err }, "sumsub token refresh refused");
        // 409: replaced by a newer attempt; 404: gone. The SDK can't continue,
        // so say so here, where "Start new test" is offered, rather than leave
        // the user on the SDK's own token error.
        if (err.status === 409 || err.status === 404) {
          setErrorMessage(t.verificationNoLongerActive);
          setState("error");
        }
      } else {
        log.error({ err }, "sumsub token refresh failed");
      }
      return "";
    }
  };

  const launchSumsubSdk = (accessToken: string) => {
    if (sdkInstanceRef.current) {
      sdkInstanceRef.current.destroy?.();
      sdkInstanceRef.current = null;
    }

    const snsWebSdkInstance = snsWebSdk
      .init(accessToken, requestNewAccessToken)
      .withConf({
        lang: lang === "nb_NO" ? "no" : "en",
        theme: resolvedTheme === "dark" ? "dark" : "light",
      })
      .withOptions({
        addViewportTag: false,
        adaptIframeHeight: true,
      })
      .on("idCheck.stepCompleted", () => {})
      .on("idCheck.onReady", () => {
        setState("verifying");
      })
      .on("idCheck.onError", (error: any) => {
        setState("error");
        setErrorMessage(`${t.verificationError} ${error.message || t.unknownError}`);
      })
      .on("idCheck.onApplicantSubmitted", () => {
        // Backend will receive applicantPending (which also finalizes a
        // sandbox review) + applicantReviewed webhooks and persist the
        // snapshot. Poll the saved value until terminal or the round exhausts.
        setPollAttempt(0);
        setIsPolling(true);
        setState("polling");
      })
      .build();

    snsWebSdkInstance.launch("#sumsub-websdk-container");
    sdkInstanceRef.current = snsWebSdkInstance;
  };

  const handleRefetchClick = () => {
    setPollAttempt(0);
    setIsPolling(true);
  };

  const handleRetry = () => {
    if (sdkInstanceRef.current) {
      sdkInstanceRef.current = null;
    }
    setSubmittedExternalUserId(null);
    setPollAttempt(0);
    setIsPolling(false);
    setErrorMessage(null);
    setIsConfigError(false);
    setState("idle");
  };

  // Discard the current result and launch a fresh verification. Goes straight
  // to the SDK (state "loading") without passing through "idle" — dropping to
  // "idle" with a terminal snapshot still renderable would let the idle→completed
  // auto-transition effect snap the widget right back to the old result.
  const handleStartNewTest = () => {
    if (sdkInstanceRef.current) {
      sdkInstanceRef.current.destroy?.();
      sdkInstanceRef.current = null;
    }
    setSubmittedExternalUserId(null);
    setPollAttempt(0);
    setIsPolling(false);
    setErrorMessage(null);
    setIsConfigError(false);
    initializeSdk();
  };

  // Handler for "Use Previous Answer"
  const handleUsePrevious = async () => {
    setIsApplyingPrevious(true);
    setReuseError(null);
    try {
      if (onApply) {
        // Advisor path: the backend spreads input+calculated and persists. The resulting
        // calculatedData flows back via setQueryData → prop, rendering the completed view.
        await onApply();
      } else if (onUsePreviousAnswer && previousAnswer) {
        // Public path (legacy): the parent's save mutation updates calculatedData.
        await onUsePreviousAnswer();
      } else {
        return;
      }
      setHasDecided(true);
      setState("completed");
    } catch (err) {
      log.error({ err }, "failed to apply previous sumsub answer");
      // Advisor refusal (e.g. instance completed in another tab) — hide the button + show message.
      if (onApply) setReuseError(t.reuseNoLongerAvailable);
    } finally {
      setIsApplyingPrevious(false);
    }
  };

  const handleStartFresh = () => {
    setHasDecided(true);
  };

  const getContainerClass = () => {
    if (hasValidationError) {
      return "border border-destructive rounded-lg p-6 space-y-4";
    }
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-4";
      case "box":
        return "rounded-lg p-6 space-y-4 bg-muted/50";
      default:
        return "space-y-4";
    }
  };

  // Choice screen
  if (showChoiceScreen) {
    const formattedDate = formatDate(parseDbTimestamp(reuseDate));

    return (
      <div className={getContainerClass()}>
        <FlowComponentHeader title={title} description={description} />
        {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}

        <div className="text-center py-4">
          <History className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-medium text-lg mb-2">{t.previousAnswerFound}</h3>
          <p className="text-sm text-muted-foreground">{t.answeredOn(formattedDate)}</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={handleUsePrevious} disabled={isApplyingPrevious} className="gap-2">
            {isApplyingPrevious ? t.applying : t.usePreviousAnswer}
          </Button>
          <Button
            variant="outline"
            onClick={handleStartFresh}
            disabled={isApplyingPrevious}
            className="gap-2"
          >
            {t.startFresh}
          </Button>
        </div>
      </div>
    );
  }

  const resultsApplicantData = renderableSnapshot?.rawData ?? null;
  const searchedAt = renderableSnapshot?.searchedAt ?? null;
  // Test→production invalidation: the prior test result is still terminal (so we
  // boot into "completed"), but its completion marker was reset. Surface a re-verification
  // banner over the preserved result; the component counts as incomplete until re-verified.
  const needsReVerification = renderableSnapshot?.hasBeenChecked === false;

  return (
    <div className={getContainerClass()}>
      <FlowComponentHeader title={title} description={description} />
      {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}
      {reuseError && <p className="text-sm text-destructive">{reuseError}</p>}
      {actingRepresentative && (
        <RepresentativeNote
          text={representativeNoteText(t.repNote, actingRepresentative, t.repGeneric)}
        />
      )}

      {/* Error state */}
      {state === "error" && (
        <div className="py-6 text-center space-y-4">
          <p className="text-destructive">{errorMessage}</p>
          {!disabled && !isConfigError && (
            <Button onClick={handleStartNewTest} variant="outline">
              {t.startNewTest}
            </Button>
          )}
        </div>
      )}

      {/* SDK container (active during loading/verifying) */}
      {(state === "loading" || state === "verifying") && !disabled && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={handleRetry} variant="outline" size="sm">
              {t.cancel}
            </Button>
          </div>
          <div id="sumsub-websdk-container" className="min-h-[600px]" />
        </div>
      )}

      {/* Polling UI — webhook hasn't landed yet (or round exhausted) */}
      {state === "polling" && !disabled && (
        <div className="py-6 space-y-4">
          <div className="flex gap-4 justify-center">
            <Button onClick={handleRefetchClick} loading={isPolling}>
              {t.refetchResult}
            </Button>
            {!isPolling && (
              <Button onClick={handleStartNewTest} variant="outline">
                {t.startNewTest}
              </Button>
            )}
          </div>
          {/* Attempt counter lives beside the button, not on it: the label holds still (QL-223) */}
          {isPolling && (
            <p className="text-center text-sm text-muted-foreground">
              {t.refetching} {pollAttempt}/{MAX_POLLS_PER_ROUND}
            </p>
          )}
        </div>
      )}

      {/* Idle state: show Start button */}
      {state === "idle" && !disabled && (
        <div className="flex justify-end">
          <Button
            onClick={handleStart}
            variant="outline"
            className="bg-white text-black hover:bg-gray-100 dark:bg-white dark:text-black dark:hover:bg-gray-100"
          >
            {t.startVerification}
          </Button>
        </div>
      )}

      {/* Completed state with results */}
      {state === "completed" && resultsApplicantData && (
        <div className="space-y-4">
          {/* Re-verification required banner — previous result stays visible below, but the
              component counts as incomplete until a fresh production verification is run. */}
          {needsReVerification && (
            <div className="rounded-md border border-warning bg-warning/10 p-3 space-y-2">
              <div className="flex items-center gap-2 text-warning font-medium">
                <span className="text-lg">⚠</span>
                {t.reVerificationRequired}
              </div>
              <p className="text-sm text-muted-foreground">{t.reVerificationExplanation}</p>
              {!disabled && (
                <Button variant="default" size="sm" onClick={handleStartNewTest}>
                  {t.reVerify}
                </Button>
              )}
            </div>
          )}
          <SumsubResults lang={lang} applicantData={resultsApplicantData} />

          {/* QL-f — a GREEN result whose details do not match the client is not
              a finished identity check. The panel asks, and blocks Submit until
              it is answered. */}
          {identityConflicts && identityConflicts.length > 0 && onResolveIdentityConflict && (
            <IdentityConflictPanel
              rows={identityConflicts}
              onResolve={onResolveIdentityConflict}
              disabled={disabled}
            />
          )}
          <div className="text-xs text-muted-foreground border-t pt-3 flex items-center justify-between">
            <span>
              {t.verification}: {resultsApplicantData.info?.firstName}{" "}
              {resultsApplicantData.info?.lastName}
              {searchedAt && (
                <>
                  {" • "}
                  <TimeAgoTooltip date={parseDbTimestamp(searchedAt)}>
                    {getRelativeTime(searchedAt, lang)}
                  </TimeAgoTooltip>
                </>
              )}
            </span>
            {!disabled && (
              <Button onClick={handleStartNewTest} variant="outline" size="sm">
                {t.startNewTest}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
